/**
 * SettingsLinkedAccountsPage
 * Plaid-linked bank accounts and external payments setup wizard.
 * Route: /settings/linked-accounts
 */

import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import { fetchExternalPaymentsHubSummary } from '../api/external-payments-hub.api';
import { fetchLinkedBankAccounts } from '../api/linked-bank-accounts.api';
import { LinkedAccountCard } from '../components/LinkedAccountCard';
import { PlaidLinkButton } from '../components/PlaidLinkButton';
import { SetupWizard } from '../components/external-payments/SetupWizard';
import { PageHeader } from '../components/layout/index';
import { Skeleton } from '../components/ui/skeleton';
import { SettingsLayout } from '../components/settings/SettingsLayout';
import type { ExternalPaymentHubSummary } from '../api/external-payments-hub.api';
import type { LinkedBankAccount } from '../api/linked-bank-accounts.api';

export function SettingsLinkedAccountsPage(): JSX.Element {
  const [hubData, setHubData] = useState<ExternalPaymentHubSummary | null>(null);
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedBankAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [hubSummary, accountsData] = await Promise.all([
        fetchExternalPaymentsHubSummary(),
        fetchLinkedBankAccounts(),
      ]);
      setHubData(hubSummary);
      setLinkedAccounts(accountsData.accounts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load linked accounts');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePlaidSuccess = () => {
    loadData();
    setWizardOpen(true);
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
  };

  if (isLoading) {
    return (
      <SettingsLayout>
        <div className="px-[var(--page-padding-x)]">
          <Skeleton className="h-7 w-56 mb-2" />
          <Skeleton className="h-4 w-80 mb-6" />
          <Skeleton className="h-24 rounded-lg mb-4" />
          <Skeleton className="h-24 rounded-lg" />
        </div>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout>
      <div data-testid="settings-linked-accounts-page">
        <div className="px-[var(--page-padding-x)]">
          <PageHeader
            title="Linked Accounts"
            subtitle="Connect bank accounts via Plaid to monitor external payments like Zelle, cash, and check."
            action={<PlaidLinkButton onSuccess={handlePlaidSuccess} />}
          />

          {/* Error banner */}
          {error && (
            <div
              className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
              role="alert"
            >
              <p className="text-destructive m-0">{error}</p>
            </div>
          )}

          {/* Connection Alerts */}
          {hubData && hubData.connectionAlerts.length > 0 && (
            <section data-testid="linked-accounts-section-alerts" className="mb-6">
              <h2 className="text-sm font-semibold text-foreground mb-2">Connection Issues</h2>
              <div className="flex flex-col gap-2">
                {hubData.connectionAlerts.map((alert) => (
                  <div
                    key={alert.linkedBankAccountId}
                    className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3"
                    role="alert"
                  >
                    <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-destructive m-0">
                        {alert.institutionName} ···{alert.accountMask}
                      </p>
                      <p className="text-xs text-destructive/80 m-0">
                        {alert.status === 'disconnected'
                          ? 'Bank account disconnected — re-authentication required'
                          : 'Connection error — please reconnect your account'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Post-link info panel */}
          {wizardOpen && (
            <div className="mb-6">
              <SetupWizard onClose={handleWizardClose} />
            </div>
          )}

          {/* Linked Accounts List */}
          <section data-testid="linked-accounts-list-section" aria-labelledby="linked-accounts-heading">
            <h2 id="linked-accounts-heading" className="text-base font-medium text-foreground mb-3">
              Linked Bank Accounts
            </h2>
            {linkedAccounts.length === 0 ? (
              <div
                data-testid="linked-accounts-empty-state"
                className="rounded-lg border border-border bg-card p-6 text-center"
              >
                <p className="text-sm text-muted-foreground mb-3">
                  No bank accounts linked yet. Link a bank account to start monitoring external
                  payments.
                </p>
                <PlaidLinkButton onSuccess={handlePlaidSuccess} />
              </div>
            ) : (
              <div
                data-testid="linked-accounts-list"
                className="flex flex-col gap-3"
              >
                {linkedAccounts.map((account) => (
                  <LinkedAccountCard
                    key={account.id}
                    account={account}
                    onUnlinked={loadData}
                    onSynced={loadData}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </SettingsLayout>
  );
}

export default SettingsLinkedAccountsPage;
